import {
  type MutableRefObject,
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type ReCaptchaV2 from 'react-google-recaptcha';
import { useParams } from 'react-router-dom';

import { type ReCaptchaAuthParams, useAuth } from '@sine/auth-client';
import { useNotification } from '@sine/sine-ui-components';

import { useDevice } from '../external/device';
import { axiosInterceptorReCaptchaRef } from '../services/auth/axios.interceptor';
import ReCaptcha from './ReCaptcha';

interface ReCaptchaAuthContextValue {
  reCaptchaRef: MutableRefObject<ReCaptchaV2 | null>;
}

const defaultReCaptchaAuthContext: ReCaptchaAuthContextValue = {
  reCaptchaRef: { current: null },
};

const ReCaptchaAuthContext = createContext(defaultReCaptchaAuthContext);

export default function ReCaptchaAuthProvider({ children }: PropsWithChildren) {
  const reCaptchaRef = useRef<ReCaptchaV2 | null>(null);

  const value = useMemo<ReCaptchaAuthContextValue>(() => ({ reCaptchaRef }), []);

  return (
    <ReCaptchaAuthContext.Provider value={value}>
      {children}

      <ReCaptcha ref={reCaptchaRef} />
    </ReCaptchaAuthContext.Provider>
  );
}

/**
 * Establish auth
 * @returns Useful url params
 */
export function useReCaptchaAuth() {
  const { addNotification } = useNotification();
  const { authState, loginViaReCaptchaAuth, refreshReCaptchaAuth } = useAuth();
  const { deviceToken } = useDevice();
  const { reCaptchaRef } = useContext(ReCaptchaAuthContext);
  const { passId, checkinToken, invitationId, teamId } = useParams();

  const [authError, setAuthError] = useState<any>();

  const handleAuthError = useCallback(
    (error: any) => {
      setAuthError(error);
      addNotification({
        heading: 'Unable to authenticate you',
        message: error?.message,
        variant: 'danger',
      });
    },
    [addNotification],
  );

  const login = useCallback(
    async (params: ReCaptchaAuthParams) => {
      try {
        if (reCaptchaRef.current) {
          reCaptchaRef.current.reset();
          const reCaptchaToken = await reCaptchaRef.current.executeAsync();

          reCaptchaToken && (await loginViaReCaptchaAuth(reCaptchaToken, params));
        }
      } catch (error: any) {
        handleAuthError(error);
      }
    },
    [handleAuthError, loginViaReCaptchaAuth, reCaptchaRef],
  );

  const refresh = useCallback(
    async (reCaptchaAuthParams: ReCaptchaAuthParams) => {
      try {
        await refreshReCaptchaAuth(async () => {
          if (reCaptchaRef.current) {
            reCaptchaRef.current.reset();
            const reCaptchaToken = (await reCaptchaRef.current.executeAsync()) || null;

            return { reCaptchaToken, reCaptchaAuthParams };
          }

          return { reCaptchaToken: null, reCaptchaAuthParams };
        });
      } catch (error: any) {
        handleAuthError(error);
      }
    },
    [refreshReCaptchaAuth, reCaptchaRef, handleAuthError],
  );

  useEffect(() => {
    if (deviceToken && authState !== 'loggedIn' && authState !== 'loading') {
      if (checkinToken) {
        login({ checkinToken });
      } else if (passId) {
        login({ passId });
      } else if (invitationId && teamId) {
        login({ invitationId, teamId });
      }
    }
  }, [addNotification, authState, checkinToken, deviceToken, invitationId, login, passId, teamId]);

  useEffect(() => {
    if (!deviceToken) {
      return;
    }

    if (checkinToken) {
      axiosInterceptorReCaptchaRef.reauthenticateViaReCaptcha = () => refresh({ checkinToken });
    } else if (passId) {
      axiosInterceptorReCaptchaRef.reauthenticateViaReCaptcha = () => refresh({ passId });
    } else if (invitationId && teamId) {
      axiosInterceptorReCaptchaRef.reauthenticateViaReCaptcha = () => refresh({ invitationId, teamId });
    }
  }, [checkinToken, deviceToken, invitationId, passId, refresh, teamId]);

  return { authState, passId, checkinToken, authError };
}
